import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Send } from 'lucide-react';
import Button from '../../components/base/Button';
import Input from '../../components/base/Input';
import InlineAlert from '../../components/common/InlineAlert';
import TextArea from '../../components/common/TextArea';
import { ACCOUNT_SUSPENDED_MESSAGE_KEY, apiErrorInfo, isAccountSuspended } from '../../utils/apiErrorInfo';
import { createAdvertisement, type Advertisement } from '../../services/advertisementService';
import { boardIssueOf, type BoardIssue } from './boardApi';

/** The backend's limits (routes/advertisements.js createSchema). */
const TITLE_MIN = 3;
const TITLE_MAX = 200;
const BODY_MIN = 5;
const BODY_MAX = 5000;
const CATEGORY_MAX = 80;

const LABEL = 'mb-1.5 block text-[0.875rem] font-medium text-[var(--pv-color-text)]';
const hintClass = (error: boolean) =>
  `mt-1.5 text-[0.875rem] leading-relaxed ${
    error ? 'text-[#C62828] dark:text-[var(--pv-color-red)]' : 'text-[var(--pv-color-text-muted)]'
  }`;

export interface NewRequestFormProps {
  token: string;
  onPosted: (ad: Advertisement) => void;
  onCancel: () => void;
  onAccessIssue: (issue: BoardIssue) => void;
}

/**
 * «طلب جديد»: title, details and an optional category. Checked on submit (not by
 * disabling the button, which would not say what is missing): the first field
 * that fails takes focus and its hint turns into the reason.
 */
export default function NewRequestForm({ token, onPosted, onCancel, onAccessIssue }: NewRequestFormProps) {
  const { t } = useTranslation(['ads', 'common']);
  const uid = useId();
  const ids = {
    heading: `${uid}-heading`,
    title: `${uid}-title`,
    titleHint: `${uid}-title-hint`,
    body: `${uid}-body`,
    bodyHint: `${uid}-body-hint`,
    category: `${uid}-category`,
  };

  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [category, setCategory] = useState('');
  // i18n keys, so a language switch re-renders the messages too.
  const [errors, setErrors] = useState<{ title?: string; body?: string }>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const titleRef = useRef<HTMLInputElement>(null);
  const bodyRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    titleRef.current?.focus();
  }, []);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (busy) return;

    const next: { title?: string; body?: string } = {};
    if (title.trim().length < TITLE_MIN) next.title = 'form.titleTooShort';
    if (body.trim().length < BODY_MIN) next.body = 'form.bodyTooShort';
    setErrors(next);
    if (next.title) {
      titleRef.current?.focus();
      return;
    }
    if (next.body) {
      bodyRef.current?.focus();
      return;
    }

    setBusy(true);
    setSubmitError(null);
    try {
      const created = await createAdvertisement(token, {
        title: title.trim(),
        body: body.trim(),
        category: category.trim() || undefined,
      });
      onPosted(created);
    } catch (error) {
      const issue = boardIssueOf(error);
      const { status } = apiErrorInfo(error);
      if (issue) onAccessIssue(issue);
      else if (status === 404) onAccessIssue('disabled');
      else if (isAccountSuspended(error)) setSubmitError(ACCOUNT_SUSPENDED_MESSAGE_KEY);
      else setSubmitError(status === 400 ? 'errors.invalid' : 'errors.post');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      aria-labelledby={ids.heading}
      className="mt-6 rounded-2xl border border-[var(--pv-border-soft)] bg-[var(--pv-color-surface-strong)] p-5 shadow-brand-xs sm:p-6"
    >
      <h2 id={ids.heading} className="text-lg font-bold text-[var(--pv-color-primary)]">
        {t('form.heading')}
      </h2>
      <form noValidate onSubmit={submit} className="mt-4 space-y-4">
        <div>
          <label htmlFor={ids.title} className={LABEL}>
            {t('form.title')}
          </label>
          <Input
            ref={titleRef}
            id={ids.title}
            value={title}
            onChange={(event) => {
              setTitle(event.target.value);
              if (errors.title) setErrors((prev) => ({ ...prev, title: undefined }));
            }}
            required
            maxLength={TITLE_MAX}
            autoComplete="off"
            placeholder={t('form.titlePlaceholder')}
            invalid={Boolean(errors.title)}
            aria-invalid={errors.title ? true : undefined}
            aria-describedby={ids.titleHint}
          />
          <p id={ids.titleHint} className={hintClass(Boolean(errors.title))}>
            {t(errors.title ?? 'form.titleHint')}
          </p>
        </div>

        <div>
          <label htmlFor={ids.body} className={LABEL}>
            {t('form.body')}
          </label>
          <TextArea
            ref={bodyRef}
            id={ids.body}
            value={body}
            onChange={(event) => {
              setBody(event.target.value);
              if (errors.body) setErrors((prev) => ({ ...prev, body: undefined }));
            }}
            required
            rows={5}
            maxLength={BODY_MAX}
            placeholder={t('form.bodyPlaceholder')}
            invalid={Boolean(errors.body)}
            aria-invalid={errors.body ? true : undefined}
            aria-describedby={ids.bodyHint}
          />
          <p id={ids.bodyHint} className={hintClass(Boolean(errors.body))}>
            {t(errors.body ?? 'form.bodyHint')}
          </p>
        </div>

        <div>
          <label htmlFor={ids.category} className={LABEL}>
            {t('form.category')}
          </label>
          <Input
            id={ids.category}
            value={category}
            onChange={(event) => setCategory(event.target.value)}
            maxLength={CATEGORY_MAX}
            autoComplete="off"
            placeholder={t('form.categoryPlaceholder')}
          />
        </div>

        {submitError ? <InlineAlert live="alert">{t(submitError)}</InlineAlert> : null}

        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button variant="secondary" onClick={onCancel} disabled={busy}>
            {t('cancel')}
          </Button>
          <Button type="submit" loading={busy}>
            {!busy ? <Send className="h-5 w-5 shrink-0 rtl:-scale-x-100" aria-hidden="true" /> : null}
            {t('form.submit')}
          </Button>
        </div>
      </form>
    </section>
  );
}
